import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, useMemo } from 'react';
import KioskItem from '../../components/KioskItem/KioskItem';
import kiosksStore from '../../store/kiosksStore';
import { KIOSKS_TABLE_COLUMN_NAME } from '../../types/kiosks';
import { Icons } from '~/assets/fonts/Icons/icons';
import { localeStore } from '~/mobx/localesStore';
import { formatImagePath } from '~/utils/formatter';
import { booleanToNumber, emptyArray } from '~/utils/helpers';
import Pagination from '~/view/components/Controls/PaginationOld/PaginationOld';
import GridTable, { TGridColumnTitle } from '~/view/containers/GridTable';
import KioskItemSkeleton from '~/view/screens/SettingsScreen/screens/Devices/screens/KiosksScreen/components/KioskItemSkeleton/KioskItemSkeleton';

const KiosksList: FC = observer((): JSX.Element => {
  const isNoFound: boolean = !kiosksStore.pagination.total && !kiosksStore.isLoading;
  const isFirstLoading: boolean = Boolean(kiosksStore.isLoading && !kiosksStore.pagination.total);

  const gridTemplate = useMemo((): string => {
    return kiosksStore.isListOfDeleted ? 'kiosk-item__grid-template-delete' : 'kiosk-item__grid-template';
  }, [kiosksStore.isListOfDeleted]);

  const columnTitles = useMemo((): TGridColumnTitle<KIOSKS_TABLE_COLUMN_NAME>[] => {
    return [
      { title: 'kiosk_id', columnName: KIOSKS_TABLE_COLUMN_NAME.ID },
      { title: 'Branch', columnName: KIOSKS_TABLE_COLUMN_NAME.BRANCH },
      { title: kiosksStore.isListOfDeleted ? '' : 'status' },
    ];
  }, [kiosksStore.isListOfDeleted]);

  const filters = useMemo(() => {
    const { sortDir, sortField } = kiosksStore.filters;
    if (sortDir && sortField) return { sortDir, sortField };
  }, [kiosksStore.filters]);

  return (
    <div className="d-flex flex-column flex-1 mt-4">
      {isNoFound ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-2">
          <img src={formatImagePath(`${Icons.NoResults}.svg`)} alt={localeStore.t('no_results_found')} className="image__no-found" />
          <p className="family-medium text-size-16 color-gray2">{localeStore.t('no_results_found')}</p>
        </div>
      ) : (
        <GridTable
          gridTemplateClass={classNames('mr-4', gridTemplate)}
          columnTitles={columnTitles}
          filters={filters}
          setFilters={kiosksStore.changeFilters}
        >
          {isFirstLoading
            ? emptyArray(3).map((_, index) => (
                <KioskItemSkeleton key={'key-' + String(index)} gridTemplate={gridTemplate} isDelete={kiosksStore.isListOfDeleted} />
              ))
            : kiosksStore.pagination.data.map((kiosk) => (
                <KioskItem
                  key={kiosk.id}
                  id={kiosk.id}
                  branchName={kiosk.branchName}
                  status={kiosk.status}
                  isDelete={booleanToNumber(kiosksStore.isListOfDeleted && kiosk.isDelete)}
                  gridTemplate={gridTemplate}
                />
              ))}
        </GridTable>
      )}

      <Pagination type="arrow" pagination={kiosksStore.pagination} loading={kiosksStore.loading} onChange={kiosksStore.fetchKiosks} />
    </div>
  );
});

export default KiosksList;
